import { createContext, useContext, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import type { Data } from '../api';

export interface AppCtx {
  d: Data;
  toast: (msg: string) => void;
  openItem: (id: number) => void;
  openAdd: (id: number) => void;
  openTake: (id: number) => void;
  openReturn: (allocId: number) => void;
}
export const Ctx = createContext<AppCtx | null>(null);
export const useApp = () => useContext(Ctx)!;

export const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));
export const pl = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;
const fmtAt = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Berlin', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const fmtDay = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Berlin', day: 'numeric', month: 'short' });
export const when = (iso: string) => fmtAt.format(new Date(iso));
export const day = (iso: string) => fmtDay.format(new Date(iso));

export function Spinner() {
  return (
    <div className="center">
      <div className="spin" />
    </div>
  );
}

export function Err({ e }: { e: string }) {
  return e ? (
    <p className="err" role="alert">
      {e}
    </p>
  ) : null;
}

/** A modal dialog; Escape or the backdrop closes it. */
export function Modal({ title, onClose, children, wide, foot }: { title: ReactNode; onClose: () => void; children: ReactNode; wide?: boolean; foot?: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current!;
    if (!d.open) d.showModal();
    return () => d.close();
  }, []);
  return (
    <dialog
      ref={ref}
      className={wide ? 'wide' : ''}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onMouseDown={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      <div className="mhead">
        <h3>{title}</h3>
        <button className="btn small" onClick={onClose} aria-label="Close">
          ✕
        </button>
      </div>
      <div className="mbody">{children}</div>
      {foot && <div className="mfoot">{foot}</div>}
    </dialog>
  );
}

/**
 * Dropdown like in Excel: pick one of the options or type a new value.
 * onCommit gets the chosen or typed value; Escape cancels.
 */
export function Combo({
  value,
  list,
  onCommit,
  onCancel,
  placeholder,
  autoFocus,
  allowNew = true,
  allowEmpty = true,
  live,
}: {
  value: string;
  list: string[];
  onCommit: (v: string) => void;
  onCancel?: () => void;
  placeholder?: string;
  autoFocus?: boolean;
  allowNew?: boolean;
  allowEmpty?: boolean;
  /** In forms: report every keystroke, so a typed value counts without pressing Enter. */
  live?: boolean;
}) {
  const [text, setText] = useState(value);
  const [open, setOpen] = useState(!!autoFocus);
  const [hi, setHi] = useState(0);
  const id = useId();
  const box = useRef<HTMLDivElement>(null);
  const q = text.trim().toLowerCase();
  const shown = list.filter((o) => !q || o.toLowerCase().includes(q) || text === value).slice(0, 60);
  const exact = list.some((o) => o.toLowerCase() === q);
  const canNew = allowNew && !!q && !exact;
  const rows: { v: string; label: ReactNode }[] = [
    ...(canNew ? [{ v: text.trim(), label: <i>Add “{text.trim()}”</i> }] : []),
    ...(allowEmpty && value ? [{ v: '', label: <span className="meta">(empty)</span> }] : []),
    ...shown.map((o) => ({ v: o, label: o })),
  ];
  const commit = (v: string) => {
    setOpen(false);
    onCommit(v);
  };
  useEffect(() => {
    const out = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) {
        setOpen(false);
        onCancel?.();
      }
    };
    document.addEventListener('mousedown', out);
    return () => document.removeEventListener('mousedown', out);
  }, [onCancel]);
  return (
    <div className="combo" ref={box}>
      <input
        value={text}
        placeholder={placeholder}
        autoFocus={autoFocus}
        role="combobox"
        aria-expanded={open}
        aria-controls={id}
        onFocus={(e) => {
          setOpen(true);
          e.target.select();
        }}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
          setHi(0);
          if (live) onCommit(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') (e.preventDefault(), setHi((h) => Math.min(h + 1, rows.length - 1)));
          else if (e.key === 'ArrowUp') (e.preventDefault(), setHi((h) => Math.max(h - 1, 0)));
          else if (e.key === 'Enter') {
            e.preventDefault();
            const r = open ? rows[hi] : undefined;
            if (r) commit(r.v);
            else if (exact) commit(list.find((o) => o.toLowerCase() === q)!);
            else if (allowNew || !q) commit(text.trim());
          } else if (e.key === 'Escape') {
            e.stopPropagation();
            e.preventDefault();
            setOpen(false);
            onCancel?.();
          }
        }}
      />
      {open && rows.length > 0 && (
        <ul className="combo-list" id={id} role="listbox">
          {rows.map((r, i) => (
            <li
              key={`${i}-${r.v}`}
              role="option"
              aria-selected={i === hi}
              className={i === hi ? 'hi' : ''}
              onMouseDown={(e) => {
                e.preventDefault();
                commit(r.v);
              }}
              onMouseEnter={() => setHi(i)}
            >
              {r.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Place › shelf picker as one select. Value is "", "p:<placeId>" or "s:<shelfId>". */
export function PlaceSelect({
  placeId,
  shelfId,
  onChange,
  autoFocus,
  onBlur,
}: {
  placeId: number | null;
  shelfId: number | null;
  onChange: (placeId: number | null, shelfId: number | null) => void;
  autoFocus?: boolean;
  onBlur?: () => void;
}) {
  const { d } = useApp();
  const v = shelfId != null ? `s:${shelfId}` : placeId != null ? `p:${placeId}` : '';
  return (
    <select
      value={v}
      autoFocus={autoFocus}
      onBlur={onBlur}
      onKeyDown={(e) => e.key === 'Escape' && onBlur?.()}
      onChange={(e) => {
        const [k, n] = e.target.value.split(':');
        if (k === 's') onChange(d.shelfById.get(Number(n))!.place.id, Number(n));
        else if (k === 'p') onChange(Number(n), null);
        else onChange(null, null);
      }}
    >
      <option value="">— no place —</option>
      {d.b.places.map((p) => (
        <optgroup key={p.id} label={p.name}>
          <option value={`p:${p.id}`}>{p.name} (no shelf)</option>
          {p.shelves.map((s) => (
            <option key={s.id} value={`s:${s.id}`}>
              {p.name} › {s.name}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

/** Turns a typed quantity into a number ("2,5" works too). */
export const num = (s: string) => Number(String(s).replace(',', '.').trim());
